import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from "lucide-react";
import { SITE, HOURS, isOpenNow, waLink, API } from "@/lib/site";

export const Contacts = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const open = isOpenNow();

  const submit = (e) => {
    e.preventDefault();
    const msg = `Buongiorno ${SITE.shortName}, sono ${form.name} (tel. ${form.phone}). ${form.message}`;
    fetch(`${API}/requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "contatto", name: form.name, phone: form.phone, message: form.message }),
    }).catch(() => {});
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  const input =
    "w-full bg-[#161D2B] border border-border rounded-xl px-4 py-3.5 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-amber-500/60";

  return (
    <section id="contatti" className="py-16 md:py-24 bg-[#0D1119] border-t border-border" data-testid="contacts-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-amber-500 mb-3">04 / Contatti</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">
            Vieni a trovarci in sede
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <a
                data-testid="contact-address-card"
                href={SITE.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#161D2B] border border-border rounded-xl p-5 hover:border-amber-500/40 transition-colors block"
              >
                <MapPin className="w-5 h-5 text-amber-500 mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gray-500">Indirizzo</p>
                <p className="mt-1.5 text-sm font-medium leading-relaxed">{SITE.address}</p>
              </a>
              <div className="bg-[#161D2B] border border-border rounded-xl p-5">
                <Phone className="w-5 h-5 text-amber-500 mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gray-500">Telefono</p>
                <a data-testid="contact-phone-sales" href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="mt-1.5 block text-sm font-medium hover:text-amber-500 transition-colors">
                  {SITE.phone} <span className="text-gray-500">· Sede</span>
                </a>
                <a data-testid="contact-phone-mobile" href={`tel:${SITE.mobile.replace(/\s/g, "")}`} className="block text-sm font-medium hover:text-amber-500 transition-colors">
                  {SITE.mobile} <span className="text-gray-500">· Mobile/WhatsApp</span>
                </a>
              </div>
              <a
                data-testid="contact-email-card"
                href={`mailto:${SITE.email}`}
                className="bg-[#161D2B] border border-border rounded-xl p-5 hover:border-amber-500/40 transition-colors block"
              >
                <Mail className="w-5 h-5 text-amber-500 mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gray-500">Email</p>
                <p className="mt-1.5 text-sm font-medium">{SITE.email}</p>
              </a>
              <div className="bg-[#161D2B] border border-border rounded-xl p-5" data-testid="contact-hours-card">
                <div className="flex items-center justify-between mb-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span
                    data-testid="contact-open-badge"
                    className={`text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                      open
                        ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                        : "text-red-400 border-red-500/40 bg-red-500/10"
                    }`}
                  >
                    {open ? "Aperto ora" : "Chiuso"}
                  </span>
                </div>
                {HOURS.map((h) => (
                  <div key={h.d} className="flex justify-between text-xs py-1 text-gray-400">
                    <span>{h.d}</span>
                    <span className="text-gray-200">{h.h}</span>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submit} className="bg-[#161D2B] border border-border rounded-xl p-6 space-y-4" data-testid="contact-form">
              <p className="font-mono2 text-[11px] uppercase tracking-widest text-amber-500">
                Scrivici — rispondiamo su WhatsApp
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="contact-form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome e Cognome"
                  className={input}
                />
                <input
                  data-testid="contact-form-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Telefono"
                  className={input}
                />
              </div>
              <textarea
                data-testid="contact-form-message"
                required
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Come possiamo aiutarti? (test drive, permuta, finanziamento…)"
                className={input}
              />
              <button
                data-testid="contact-form-submit"
                type="submit"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold px-6 py-3.5 rounded-full transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.35)]"
              >
                <Send className="w-4 h-4" /> Invia via WhatsApp
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="map-dark rounded-xl overflow-hidden border border-border min-h-[420px] lg:min-h-full"
            data-testid="contact-map"
          >
            <iframe
              title="Mappa Manno Auto - Cattolica Eraclea"
              src={SITE.mapsEmbed}
              className="w-full h-full min-h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>

        <a
          data-testid="contact-whatsapp-banner"
          href={waLink(`Buongiorno ${SITE.shortName}, vorrei maggiori informazioni su un veicolo in catalogo.`)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-10 flex items-center justify-center gap-3 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 rounded-xl py-5 font-medium transition-colors"
        >
          <MessageCircle className="w-5 h-5" />
          Preferisci scriverci subito? Apri la chat WhatsApp
        </a>
      </div>
    </section>
  );
};
